/**
 * Painel fiscal (Tributação › Painel): transforma o resumo por cliente (RPC fiscal_painel_resumo) em alertas — frases com
 * prioridade e um link que leva direto ao ponto — e nos indicadores dos cartões. Puro: sem rede e sem DOM.
 *
 * "Pendente" é o que ainda não foi marcado como validado nas telas; conforme a equipe valida, os números caem.
 */

export type ResumoConfronto = {
  lotes: number;
  notas: number;
  itens: number;
  ultimo_em: string | null;
  avaliado_em: string | null;
  ultimo_lote_id: string | null;
  versoes: (string | null)[];
  regras_erro: number;
  regras_erro_pendentes: number;
  itens_erro_pendentes: number;
  regras_alerta: number;
  regras_alerta_pendentes: number;
  itens_alerta_pendentes: number;
  principais: { regra: string; itens: number }[];
};
export type ResumoProtheus = { grupos: number; cobertos: number; validados: number; pendentes: number; sem_regra: number; valor_diverge: number; inconclusivo: number; avaliado_em: string | null };
export type ResumoSped = {
  concluidos: number;
  falharam: number;
  em_andamento: number;
  com_erro: number;
  erros: number;
  alertas: number;
  ultimo_periodo: string | null;
  sem_conciliacao: number;
  divergencias_conciliacao: number;
  cobertura_minima: number | null;
};
export type ResumoXmls = {
  certificado_valido_ate: string | null;
  ultima_consulta: string | null;
  faltam_na_sefaz: number;
  recebidas: number;
  so_resumo: number;
  emitidas: number;
  emitidas_em: string | null;
  sem_confronto: number;
};
export type ResumoCliente = { cnpj: string; nome: string | null; confronto: ResumoConfronto | null; protheus: ResumoProtheus | null; sped: ResumoSped | null; xmls: ResumoXmls | null };

export type FonteAgenda = { codigo: string; nome: string; agenda_frequencia: string | null; agenda_proxima: string | null };

export type Nivel = 'critico' | 'alto' | 'medio';
export type Area = 'confronto' | 'protheus' | 'sped' | 'xmls' | 'base';
export type Alerta = { id: string; nivel: Nivel; area: Area; cnpj: string | null; cliente: string; texto: string; detalhe?: string; acao: string; link: string };

export type Situacao = 'critico' | 'atencao' | 'ok' | 'sem_dados';
export type Indicador = { area: Exclude<Area, 'base'>; titulo: string; valor: string; rotulo: string; detalhe: string; tom: 'ruim' | 'atencao' | 'bom' | 'neutro'; link: string };
export type ClienteAvaliado = { resumo: ResumoCliente; situacao: Situacao; alertas: Alerta[]; indicadores: Indicador[] };

const DIA = 24 * 60 * 60 * 1000;
const PESO: Record<Nivel, number> = { critico: 0, alto: 1, medio: 2 };
const n = (v: number) => v.toLocaleString('pt-BR');
const pl = (v: number, um: string, varios: string) => `${n(v)} ${v === 1 ? um : varios}`;
const dataBr = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—');

/** Links diretos: as telas leem ?cliente= (e o que mais vier) e já abrem no ponto. */
export const linkConfronto = (cnpj: string, c: ResumoConfronto | null) => `/tributacao/confronto?cliente=${cnpj}${c?.lotes === 1 && c.ultimo_lote_id ? `&lote=${c.ultimo_lote_id}` : ''}`;
export const linkBaixados = (cnpj: string) => `/tributacao/confronto?cliente=${cnpj}&baixados=1`;
export const linkProtheus = (cnpj: string) => `/tributacao/diagnostico-protheus?cliente=${cnpj}`;
export const linkSped = (cnpj: string) => `/tributacao/sped/validacao?cliente=${cnpj}`;
/** Certificado fica no cadastro do cliente (Tokens API); download e notas guardadas ficam no Confronto de notas. */
export const linkXmls = (cnpj: string, aba: 'certificado' | 'download' = 'download') =>
  aba === 'certificado' ? `/tributacao/tokens-api?cliente=${cnpj}&aba=certificado` : `/tributacao/confronto?cliente=${cnpj}&xmls=1`;

export function avaliarCliente(r: ResumoCliente, versaoMotorAtual: string, agora = Date.now()): ClienteAvaliado {
  const alertas: Alerta[] = [];
  const cliente = r.nome || r.cnpj;
  const add = (nivel: Nivel, area: Area, chave: string, texto: string, acao: string, link: string, detalhe?: string) =>
    alertas.push({ id: `${r.cnpj}:${chave}`, nivel, area, cnpj: r.cnpj, cliente, texto, detalhe, acao, link });

  const c = r.confronto;
  if (c) {
    if (c.regras_erro_pendentes > 0) {
      add('alto', 'confronto', 'conf-erro', `${pl(c.regras_erro_pendentes, 'regra com erro', 'regras com erro')} no confronto ainda não ${c.regras_erro_pendentes === 1 ? 'validada' : 'validadas'} (${pl(c.itens_erro_pendentes, 'item', 'itens')})`, 'Validar', linkConfronto(r.cnpj, c), c.principais.length ? `Maiores: ${c.principais.map((p) => `${p.regra} (${n(p.itens)})`).join(', ')}` : undefined);
    }
    if (c.regras_alerta_pendentes > 0) {
      add('medio', 'confronto', 'conf-alerta', `${pl(c.regras_alerta_pendentes, 'alerta do confronto', 'alertas do confronto')} sem validação (${pl(c.itens_alerta_pendentes, 'item', 'itens')})`, 'Revisar', linkConfronto(r.cnpj, c));
    }
    if (c.versoes.some((v) => v !== versaoMotorAtual)) {
      add('medio', 'confronto', 'conf-versao', 'Há confronto avaliado com uma versão anterior das regras', 'Reavaliar', `/tributacao/confronto?cliente=${r.cnpj}`, 'Use "Reavaliar todos" para aplicar as regras e a base atuais.');
    }
  }

  const p = r.protheus;
  if (p) {
    if (p.sem_regra > 0) add('alto', 'protheus', 'prot-sem-regra', `${pl(p.sem_regra, 'sugestão sem regra', 'sugestões sem regra')} no Configurador de Tributos (Protheus) a tratar`, 'Abrir diagnóstico', linkProtheus(r.cnpj));
    // Regras em conflito (diagnóstico 2.6): o que sobra das pendências fora das situações contadas pelo banco.
    const conflito = Math.max(0, p.pendentes - p.sem_regra - p.valor_diverge - p.inconclusivo);
    if (conflito > 0) add('alto', 'protheus', 'prot-conflito', `${pl(conflito, 'tributo com regras em conflito', 'tributos com regras em conflito')} no Configurador (mais de uma regra casa com a mesma operação)`, 'Abrir diagnóstico', linkProtheus(r.cnpj));
    if (p.valor_diverge > 0) add('alto', 'protheus', 'prot-diverge', `${pl(p.valor_diverge, 'regra do Protheus', 'regras do Protheus')} com valor diferente do XML a tratar`, 'Abrir diagnóstico', linkProtheus(r.cnpj));
    if (p.inconclusivo > 0) add('medio', 'protheus', 'prot-inconclusivo', `${pl(p.inconclusivo, 'diagnóstico inconclusivo', 'diagnósticos inconclusivos')} no Protheus (cadastro de produto ou participante ausente)`, 'Abrir diagnóstico', linkProtheus(r.cnpj));
    if (c?.avaliado_em && p.avaliado_em && new Date(p.avaliado_em).getTime() < new Date(c.avaliado_em).getTime()) {
      add('medio', 'protheus', 'prot-antigo', `Diagnóstico Protheus de ${dataBr(p.avaliado_em)} é anterior ao último confronto (${dataBr(c.avaliado_em)})`, 'Ver diagnóstico', linkProtheus(r.cnpj), 'Rode de novo no Protheus (FacilFISA › Diagnosticar cenários do portal).');
    }
  }

  const s = r.sped;
  if (s) {
    if (s.falharam > 0) add('alto', 'sped', 'sped-falha', `${pl(s.falharam, 'arquivo de SPED falhou', 'arquivos de SPED falharam')} no processamento`, 'Ver SPED', linkSped(r.cnpj));
    if (s.com_erro > 0) add('alto', 'sped', 'sped-erro', `${pl(s.com_erro, 'SPED com erro', 'SPEDs com erro')} de validação (${pl(s.erros, 'erro', 'erros')})`, 'Ver SPED', linkSped(r.cnpj));
    if (s.divergencias_conciliacao > 0) add('medio', 'sped', 'sped-conc', `Conciliação EFD × XML: ${pl(s.divergencias_conciliacao, 'nota divergente', 'notas divergentes')}`, 'Ver SPED', linkSped(r.cnpj), s.cobertura_minima != null ? `Menor cobertura: ${Number(s.cobertura_minima).toLocaleString('pt-BR')}%` : undefined);
    if (s.sem_conciliacao > 0) add('medio', 'sped', 'sped-sem-conc', `${pl(s.sem_conciliacao, 'SPED ainda sem conciliação', 'SPEDs ainda sem conciliação')} com os XMLs`, 'Conciliar', linkSped(r.cnpj));
  }

  const x = r.xmls;
  if (x) {
    if (x.certificado_valido_ate) {
      const dias = Math.floor((new Date(x.certificado_valido_ate).getTime() - agora) / DIA);
      if (dias < 0) add('critico', 'xmls', 'cert-vencido', `Certificado digital vencido em ${dataBr(x.certificado_valido_ate)}: o download de XMLs está parado`, 'Trocar certificado', linkXmls(r.cnpj, 'certificado'));
      else if (dias <= 30) add(dias <= 7 ? 'alto' : 'medio', 'xmls', 'cert-vencendo', `Certificado digital vence ${dias === 0 ? 'hoje' : `em ${pl(dias, 'dia', 'dias')}`} (${dataBr(x.certificado_valido_ate)})`, 'Trocar certificado', linkXmls(r.cnpj, 'certificado'));
    }
    if (x.sem_confronto > 0) add('medio', 'xmls', 'xml-sem-confronto', `${pl(x.sem_confronto, 'nota com XML guardado ainda não entrou', 'notas com XML guardado ainda não entraram')} em nenhum confronto`, 'Confrontar', linkBaixados(r.cnpj));
    if (x.faltam_na_sefaz > 0) add('medio', 'xmls', 'xml-faltam', `Download da SEFAZ incompleto: faltam cerca de ${pl(x.faltam_na_sefaz, 'documento', 'documentos')}`, 'Baixar', linkXmls(r.cnpj));
  }

  alertas.sort((a, b) => PESO[a.nivel] - PESO[b.nivel]);
  const temDados = Boolean(c || p || s || (x && (x.recebidas || x.emitidas || x.certificado_valido_ate)));
  const situacao: Situacao = !temDados ? 'sem_dados' : alertas.some((a) => a.nivel !== 'medio') ? 'critico' : alertas.length ? 'atencao' : 'ok';

  const confPend = (c?.regras_erro_pendentes ?? 0) + (c?.regras_alerta_pendentes ?? 0);
  const confTotal = (c?.regras_erro ?? 0) + (c?.regras_alerta ?? 0);
  const spedProblemas = (s?.com_erro ?? 0) + (s?.falharam ?? 0);
  const indicadores: Indicador[] = [
    {
      area: 'confronto',
      titulo: 'Confronto de notas',
      valor: c ? n(confPend) : '—',
      rotulo: c ? (confPend === 1 ? 'regra pendente' : 'regras pendentes') : 'sem confronto',
      detalhe: c ? `${n(confTotal - confPend)} de ${n(confTotal)} validadas · ${pl(c.notas, 'nota', 'notas')} em ${pl(c.lotes, 'confronto', 'confrontos')}` : 'Envie XMLs ou use os XMLs baixados',
      tom: !c ? 'neutro' : (c.regras_erro_pendentes > 0 ? 'ruim' : confPend > 0 ? 'atencao' : 'bom'),
      link: linkConfronto(r.cnpj, c),
    },
    {
      area: 'sped',
      titulo: 'SPED',
      valor: s ? n(spedProblemas) : '—',
      rotulo: s ? (spedProblemas === 1 ? 'arquivo com erro' : 'arquivos com erro') : 'sem SPED',
      detalhe: s
        ? `${pl(s.concluidos, 'validado', 'validados')}${s.ultimo_periodo ? ` · até ${dataBr(s.ultimo_periodo)}` : ''}${s.cobertura_minima != null ? ` · cobertura ${Number(s.cobertura_minima).toLocaleString('pt-BR')}%` : s.sem_conciliacao ? ' · sem conciliação' : ''}`
        : 'Envie a EFD ICMS/IPI para validar',
      tom: !s ? 'neutro' : (spedProblemas > 0 ? 'ruim' : s.divergencias_conciliacao > 0 || s.sem_conciliacao > 0 ? 'atencao' : 'bom'),
      link: linkSped(r.cnpj),
    },
    {
      area: 'protheus',
      titulo: 'Diagnóstico Protheus',
      valor: p ? n(p.pendentes) : '—',
      rotulo: p ? (p.pendentes === 1 ? 'sugestão pendente' : 'sugestões pendentes') : 'sem diagnóstico',
      detalhe: p ? `${n(p.validados)} validadas · ${n(p.cobertos)} cobertas pelas regras · ${dataBr(p.avaliado_em)}` : 'Rode o diagnóstico pelo FacilFISA',
      tom: !p ? 'neutro' : (p.sem_regra + p.valor_diverge > 0 ? 'ruim' : p.pendentes > 0 ? 'atencao' : 'bom'),
      link: linkProtheus(r.cnpj),
    },
    {
      area: 'xmls',
      titulo: 'XMLs',
      valor: x && (x.recebidas || x.emitidas) ? n(x.sem_confronto) : '—',
      rotulo: x && (x.recebidas || x.emitidas) ? 'fora de confronto' : 'sem XMLs guardados',
      detalhe: x && (x.recebidas || x.emitidas || x.certificado_valido_ate)
        ? `${n(x.recebidas)} recebidas${x.so_resumo ? ` (${n(x.so_resumo)} só resumo)` : ''} · ${n(x.emitidas)} emitidas${x.certificado_valido_ate ? ` · cert. até ${dataBr(x.certificado_valido_ate)}` : ' · sem certificado'}`
        : 'Envie o certificado A1 para baixar da SEFAZ',
      tom: !x || !(x.recebidas || x.emitidas) ? 'neutro' : (alertas.some((a) => a.area === 'xmls' && a.nivel !== 'medio') ? 'ruim' : alertas.some((a) => a.area === 'xmls') ? 'atencao' : 'bom'),
      link: x && x.sem_confronto > 0 ? linkBaixados(r.cnpj) : linkXmls(r.cnpj),
    },
  ];
  return { resumo: r, situacao, alertas, indicadores };
}

/** Alertas da base de conhecimento (não são de um cliente): fontes com a verificação agendada vencida. */
export function alertasDaBase(fontes: FonteAgenda[], agora = Date.now()): Alerta[] {
  const vencidas = fontes.filter((f) => f.agenda_proxima && !['a_definir', 'sob_demanda'].includes(f.agenda_frequencia ?? 'a_definir') && new Date(f.agenda_proxima).getTime() < agora - DIA);
  if (!vencidas.length) return [];
  return [
    {
      id: 'base:fontes-vencidas',
      nivel: 'medio',
      area: 'base',
      cnpj: null,
      cliente: 'Base de conhecimento',
      texto: `${pl(vencidas.length, 'fonte com a verificação agendada vencida', 'fontes com a verificação agendada vencida')}`,
      detalhe: vencidas.slice(0, 4).map((f) => f.nome).join(', ') + (vencidas.length > 4 ? '…' : ''),
      acao: 'Ver fontes',
      link: '/tributacao/base-conhecimento/fontes',
    },
  ];
}

const ORDEM_SITUACAO: Record<Situacao, number> = { critico: 0, atencao: 1, ok: 2, sem_dados: 3 };

/** Painel inteiro: clientes avaliados (os que pedem atenção primeiro) e a lista única de alertas, por prioridade. */
export function montarPainel(resumos: ResumoCliente[], fontes: FonteAgenda[], versaoMotorAtual: string, agora = Date.now()) {
  const clientes = resumos
    .map((r) => avaliarCliente(r, versaoMotorAtual, agora))
    .sort((a, b) => ORDEM_SITUACAO[a.situacao] - ORDEM_SITUACAO[b.situacao] || b.alertas.length - a.alertas.length || (a.resumo.nome ?? '').localeCompare(b.resumo.nome ?? '', 'pt-BR'));
  const alertas = [...clientes.flatMap((c) => c.alertas), ...alertasDaBase(fontes, agora)].sort((a, b) => PESO[a.nivel] - PESO[b.nivel]);
  return { clientes, alertas };
}
